import "./styles.scss";

import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import useResponsive from "libs/helpers/useResponsive";
import usePagination from "libs/helpers/usePagination";
import countries from "libs/countries.json";
import { users_sorting } from "libs/sorting";

import Loader from "components/Loader";
import Pagination from "components/Pagination";
import ScrollTopMobile from "components/ScrollTopMobile";
import Select from "components/form/Select";

import { useUsers } from "context/admin/users";

import { ReactComponent as ViewIcon } from "assets/icons/new/view.svg";
import { ReactComponent as NoDataIcon } from "assets/icons/no-data.svg";

const mainClass = "users-admin-tiles";

const UsersTiles = ({ data, loading }) => {
  const { t } = useTranslation();
  const { max } = useResponsive();
  const navigate = useNavigate();
  const { search } = useLocation();
  const { filters, filterHandler } = useUsers();
  const perPage = max.sm ? 4 : 12;
  const { currentPage, totalPages, handlePageChange, paginatedData } =
    usePagination(data, perPage);

  return (
    <div className={mainClass}>
      <div className={`${mainClass}__header`}>
        <div className={`${mainClass}__sort`}>
          <Select.Sort
            key={filters?.sort || "default"}
            initial={filters?.sort}
            options={users_sorting}
            forceBottom
            onChange={(option) => filterHandler("sort", option.value)}
          />
        </div>
      </div>
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          key={currentPage}
          className={`${mainClass}__row`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ type: "tween", duration: 0.3 }}
        >
          {loading ? (
            <div className={`${mainClass}__loader`}>
              <Loader color="blue" center size="large" />
            </div>
          ) : paginatedData.length > 0 ? (
            paginatedData.map(
              ({
                _id,
                first_name,
                last_name,
                email,
                phone,
                websites,
                type,
              }) => {
                const phoneNumber = phone?.number
                  ? `${
                      countries?.find((item) => item?.code === phone?.code)
                        ?.dialling_code || ""
                    } ${phone.number}`
                  : "-";
                const detailsPath =
                  type === "webmaster"
                    ? `/webmasters/${_id}/account-settings`
                    : `/users/${_id}${search}`;

                return (
                  <div className={`${mainClass}__tile`} key={_id}>
                    <h4>{`${first_name} ${last_name}`}</h4>
                    <div className={`${mainClass}__tile-summary`}>
                      <span>{t("Email")}</span>
                      {email ? (
                        <a href={`mailto:${email}`}>{email}</a>
                      ) : (
                        <strong>-</strong>
                      )}
                    </div>
                    <div className={`${mainClass}__tile-summary`}>
                      <span>{t("Phone")}</span>
                      <strong>{phoneNumber}</strong>
                    </div>
                    <div className={`${mainClass}__tile-summary`}>
                      <span>{t("Account type")}</span>
                      <strong>{t(type)}</strong>
                    </div>
                    <div className={`${mainClass}__tile-websites`}>
                      <span>{t("Website [SHORT]")}</span>
                      {websites?.length > 0 ? (
                        <ul>
                          {websites.map((website, index) => (
                            <li key={website?._id}>
                              <Link to={`/clients/${website?._id}`}>
                                {website?.website}
                              </Link>
                              {index !== websites.length - 1 && ", "}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <strong>-</strong>
                      )}
                    </div>
                    <button
                      type="button"
                      className={`${mainClass}__tile-action`}
                      onClick={() => navigate(detailsPath)}
                    >
                      {t("View")}
                      <ViewIcon aria-hidden="true" />
                    </button>
                  </div>
                );
              },
            )
          ) : (
            <div className={`${mainClass}__no-data`}>
              <NoDataIcon aria-hidden="true" />
              <p>{t("No data")}</p>
            </div>
          )}
        </motion.div>
      </AnimatePresence>
      {totalPages > 1 && !loading && (
        <div className={`${mainClass}__pagination`}>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onChange={handlePageChange}
          />
        </div>
      )}
      <div className={`${mainClass}__scroll`}>
        <ScrollTopMobile />
      </div>
    </div>
  );
};

export default UsersTiles;
